import { toast } from "react-toastify";
import { useAuthStore } from "@/shared/store";

export type ApiFetchRequest = {
	readonly uri: string;
	readonly method: string;
	readonly body?: unknown;
	readonly query?: Record<string, unknown>;
};

export type ApiError = {
	readonly code: string;
	readonly message: string;
};

export type ApiResponse<T> = {
	readonly data: T | null;
	readonly error: ApiError | null;
};

export type ApiErrorBody = ApiResponse<never> & {
	readonly data: null;
};

const apiBaseUrl = process.env.PUBLIC_API_URL;

if (apiBaseUrl === undefined || apiBaseUrl.length === 0) {
	throw new Error("Не задана переменная окружения PUBLIC_API_URL.");
}

export const apiUrl = (uri: string): URL => new URL(uri, `${apiBaseUrl}/`);

/** Маленькие тела не жмём: заголовок gzip съест всю выгоду. */
const MIN_COMPRESSED_BODY = 1024;

// zstd браузерный CompressionStream не умеет, поэтому запросы — gzip.
const gzip = (text: string): Promise<Blob> =>
	new Response(new Blob([text]).stream().pipeThrough(new CompressionStream("gzip"))).blob();

export const apiFetch = async <T>(request: ApiFetchRequest): Promise<T> => {
	const url = apiUrl(request.uri);
	for (const [name, value] of Object.entries(request.query ?? {})) {
		if (value !== null) url.searchParams.set(name, String(value));
	}

	const accessToken = useAuthStore.getState().accessToken;
	const isFormData = request.body instanceof FormData;
	const json = request.body === undefined || isFormData ? null : JSON.stringify(request.body);
	const compressed = json !== null && json.length >= MIN_COMPRESSED_BODY;
	const headers = {
		...(json === null ? {} : { "Content-Type": "application/json" }),
		...(compressed ? { "Content-Encoding": "gzip" } : {}),
		...(accessToken === null ? {} : { Authorization: `Bearer ${accessToken}` }),
	};

	let response: Response;
	try {
		response = await fetch(url, {
			method: request.method,
			headers: headers,
			body:
				json === null
					? (request.body as FormData | undefined)
					: compressed
						? await gzip(json)
						: json,
		});
	} catch {
		return errorResponseWithToast("NETWORK_ERROR", "Не удалось подключиться к серверу.");
	}

	if (response.status === 204) return successResponse<T>(null);

	let responseBody: unknown;
	try {
		responseBody = await response.json();
	} catch {
		return errorResponseWithToast("INVALID_RESPONSE", "Сервер вернул некорректный ответ.");
	}

	if (isApiResponse(responseBody)) {
		if (responseBody.error !== null) toast.error(responseBody.error.message);
		return responseBody as T;
	}
	if (!response.ok) {
		return errorResponseWithToast(
			"HTTP_ERROR",
			response.statusText || "Не удалось выполнить запрос.",
		);
	}
	return successResponse<T>(responseBody);
};

const errorResponse = <T>(code: string, message: string): T =>
	({ data: null, error: { code, message } }) as T;

const errorResponseWithToast = <T>(code: string, message: string): T => {
	toast.error(message);
	return errorResponse(code, message);
};

const successResponse = <T>(data: unknown): T => ({ data, error: null }) as T;

const isApiResponse = (value: unknown): value is ApiResponse<unknown> =>
	typeof value === "object" && value !== null && "data" in value && "error" in value;
